import fs from 'node:fs/promises'
import path from 'node:path'
import { fileURLToPath } from 'node:url'
import type { Command } from 'commander'

import { getProgram } from '@/program'

const __filename = fileURLToPath(import.meta.url)
const __dirname = path.dirname(__filename)

/**
 * Recursively generates documentation for a command and its subcommands
 * @param command - The command to generate documentation for
 * @param depth - The current nesting level of the command (default: 0)
 * @returns Array of documentation objects containing title, content and depth
 * @example
 * const cmd = new Command('test')
 * cmd.description('A test command')
 * const subCmd = new Command('sub')
 * subCmd.description('A subcommand')
 * cmd.addCommand(subCmd)
 * const docs = generateDocs(cmd)
 * // Returns:
 * // [
 * //   { title: 'test', content: 'Usage: test [options]...', depth: 0 },
 * //   { title: 'sub', content: 'Usage: test sub [options]...', depth: 1 }
 * // ]
 */
export const generateDocs = (command: Command, depth = 0) => {
  const documentation: { title: string; content: string; depth: number }[] = []
  documentation.push({
    title: command.name(),
    content: command.helpInformation(),
    depth,
  })

  const result = command.commands.flatMap((it) => generateDocs(it, depth + 1))
  documentation.push(...result)

  return documentation
}

/**
 * Generates markdown documentation for a command and its subcommands
 * @param command - The command to generate documentation for
 * @returns A markdown string containing the command documentation
 * @example
 * const cmd = new Command('test')
 * cmd.description('A test command')
 * const docs = generateCommandDocumentation(cmd)
 * // Returns:
 * // ```bash
 * // Usage: test [options]
 * //
 * // A test command
 * //
 * // Options:
 * //   -h, --help     display help for command
 * // ```
 */
export const generateCommandDocumentation = (command: Command) => {
  const documentation = generateDocs(command)

  const codeBlock = '```'
  const documentationContent: string[] = []
  for (const docs of documentation) {
    if (docs.depth !== 0) {
      documentationContent.push(`${'#'.repeat(docs.depth + 2)} ${docs.title}`)
    }
    documentationContent.push(`${codeBlock}\n${docs.content}${codeBlock}`)
    documentationContent.push('')
  }

  return documentationContent.join('\n')
}

/**
 * Generates a documentation file by combining a template with command documentation
 * @param outputPath - The path where the documentation file will be written
 * @example
 * // Generate documentation at ./docs/README.md
 * await generateDocumentationFile('./docs/README.md')
 */
export const generateDocumentationFile = async (outputPath: string) => {
  const commandDocumentation = generateCommandDocumentation(getProgram())
  const template = await fs.readFile(path.join(__dirname, 'template.md'), {
    encoding: 'utf-8',
  })

  await fs.writeFile(
    outputPath,
    template.replace('<COMMANDS>', commandDocumentation),
  )
}
